// Pulse — the brain as a heartbeat: every atom on a time axis, one lane per
// type family, dot size = content weight. "Replay growth" sweeps a cursor
// across the corpus so you watch the knowledge accumulate day by day
// (user-initiated animation; reduced-motion users get the final frame).
import { useEffect, useRef, useState } from "react";
import type { AtlasAtom } from "../../../lib/atlas-types";
import { Icon } from "../../../design/ui";
import {
  type Tokens,
  type TooltipPayload,
  rgba,
  hash01,
  atomRadius,
  ageLabel,
  fitCanvas,
  prefersReducedMotion,
  MONO_10,
} from "./atlas-canvas";

interface Props {
  atoms: AtlasAtom[];
  visible: boolean[];
  lanes: { label: string }[];
  laneIndexOf: (type: string) => number;
  colorOf: (a: AtlasAtom) => string;
  tokens: Tokens;
  selectedKey: string | null;
  onSelect: (a: AtlasAtom | null) => void;
  onHover: (h: TooltipPayload | null) => void;
}

const TAU = Math.PI * 2;
const GUTTER = 104;
const AXIS_H = 30;
const TOP = 14;
const PLAY_MS = 6500;
const DAY = 86400000;

export function PulseView(props: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [playing, setPlaying] = useState(false);
  const camRef = useRef({ kx: 1, panX: 0 });
  const tauRef = useRef<number>(Infinity); // playback cursor (epoch ms); Infinity = all shown
  const playRef = useRef<{ raf: number; startedAt: number } | null>(null);
  const hoverRef = useRef(-1);
  const dragRef = useRef({ on: false, moved: false, x: 0 });
  const rafRef = useRef(0);
  const propsRef = useRef(props);
  propsRef.current = props;
  const drawRef = useRef<() => void>(() => {});

  const domain = (): [number, number] => {
    const { atoms, visible } = propsRef.current;
    let t0 = Infinity;
    let t1 = -Infinity;
    for (let i = 0; i < atoms.length; i++) {
      if (!visible[i] || !atoms[i].created_at) continue;
      t0 = Math.min(t0, atoms[i].created_at);
      t1 = Math.max(t1, atoms[i].created_at);
    }
    if (!isFinite(t0)) {
      const now = Date.now();
      return [now - 30 * DAY, now];
    }
    // pad to whole days
    return [Math.floor(t0 / DAY) * DAY, Math.ceil((t1 + 1) / DAY) * DAY];
  };

  drawRef.current = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const [w, h] = fitCanvas(canvas);
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const { atoms, visible, lanes, laneIndexOf, colorOf, tokens: T, selectedKey } = propsRef.current;
    const cam = camRef.current;
    const [t0, t1] = domain();
    const plotW = Math.max(50, w - GUTTER - 16);
    const plotH = Math.max(50, h - TOP - AXIS_H);
    const laneH = plotH / lanes.length;
    const xOf = (t: number) => GUTTER + ((t - t0) / (t1 - t0)) * plotW * cam.kx + cam.panX;
    const tau = Math.min(tauRef.current, t1);

    ctx.fillStyle = T.surface;
    ctx.fillRect(0, 0, w, h);

    // Weekend shading + day grid + axis labels.
    const dayW = (plotW * cam.kx) / ((t1 - t0) / DAY);
    for (let t = t0; t <= t1; t += DAY) {
      const x = xOf(t);
      if (x < GUTTER - dayW || x > w) continue;
      const dow = new Date(t).getDay();
      if (dayW >= 7 && (dow === 0 || dow === 6)) {
        ctx.fillStyle = rgba(T.surfaceMuted, 0.9);
        ctx.fillRect(x, TOP, Math.min(dayW, w - x), plotH);
      }
    }
    const labelEvery = dayW >= 46 ? 1 : dayW >= 13 ? 7 : 14;
    ctx.font = MONO_10;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (let t = t0, d = 0; t <= t1; t += DAY, d++) {
      const x = xOf(t);
      if (x < GUTTER - 4 || x > w + 4) continue;
      ctx.strokeStyle = rgba(T.rowDivider, 1);
      ctx.beginPath();
      ctx.moveTo(x, TOP);
      ctx.lineTo(x, TOP + plotH);
      ctx.stroke();
      if (d % labelEvery === 0) {
        const dt = new Date(t);
        ctx.fillStyle = T.textMuted;
        ctx.fillText(
          dt.toLocaleDateString("en-NZ", { month: "short", day: "numeric" }).toUpperCase(),
          x,
          TOP + plotH + 14
        );
      }
    }

    // Lane separators + labels.
    for (let li = 0; li <= lanes.length; li++) {
      const y = TOP + li * laneH;
      ctx.strokeStyle = T.rowDivider;
      ctx.beginPath();
      ctx.moveTo(GUTTER, y);
      ctx.lineTo(w - 8, y);
      ctx.stroke();
    }
    ctx.textAlign = "left";
    for (let li = 0; li < lanes.length; li++) {
      const y = TOP + li * laneH + laneH / 2;
      ctx.fillStyle = T.textMuted;
      ctx.font = MONO_10;
      ctx.fillText(lanes[li].label.toUpperCase(), 12, y);
    }

    // Clip the plot area so pan/zoom never bleeds into the gutter.
    ctx.save();
    ctx.beginPath();
    ctx.rect(GUTTER, 0, w - GUTTER, h);
    ctx.clip();

    // Atoms.
    const popSpan = (t1 - t0) * 0.02; // pop-in window in cursor time
    for (let i = 0; i < atoms.length; i++) {
      if (!visible[i]) continue;
      const a = atoms[i];
      const t = a.created_at || t0;
      if (t > tau) continue;
      const x = xOf(t);
      if (x < GUTTER - 16 || x > w + 16) continue;
      const li = laneIndexOf(a.type);
      const y = TOP + li * laneH + laneH / 2 + (hash01(a.key) - 0.5) * laneH * 0.64;
      const pop = tau === t1 || !isFinite(tauRef.current) ? 1 : Math.min(1, Math.max(0.05, (tau - t) / popSpan));
      const r = atomRadius(a.chars, 1) * 0.85 * (0.4 + 0.6 * pop);
      const color = colorOf(a);
      ctx.globalAlpha = pop;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, TAU);
      if (!a.status || a.status === "active" || a.status === "draft") {
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = T.surface;
        ctx.lineWidth = 1;
        ctx.stroke();
      } else {
        ctx.fillStyle = rgba(color, 0.13);
        ctx.fill();
        ctx.strokeStyle = rgba(color, 0.55);
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
      if (selectedKey && a.key === selectedKey) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(x, y, r + 3, 0, TAU);
        ctx.stroke();
      }
      if (hoverRef.current === i) {
        ctx.fillStyle = rgba(color, 0.16);
        ctx.beginPath();
        ctx.arc(x, y, r + 6, 0, TAU);
        ctx.fill();
      }
    }

    // Playback cursor.
    if (isFinite(tauRef.current) && tau < t1) {
      const x = xOf(tau);
      ctx.strokeStyle = T.teal;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(x, TOP);
      ctx.lineTo(x, TOP + plotH);
      ctx.stroke();
      ctx.font = MONO_10;
      ctx.textAlign = "center";
      ctx.fillStyle = T.tealInk;
      ctx.fillText(new Date(tau).toLocaleDateString("en-NZ", { month: "short", day: "numeric" }).toUpperCase(), x, TOP - 6 + 2);
    }
    ctx.restore();
  };

  const schedule = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      drawRef.current();
    });
  };

  useEffect(schedule);

  const stopPlayback = (jumpToEnd: boolean) => {
    if (playRef.current) {
      cancelAnimationFrame(playRef.current.raf);
      playRef.current = null;
    }
    if (jumpToEnd) tauRef.current = Infinity;
    setPlaying(false);
    schedule();
  };

  const startPlayback = () => {
    if (prefersReducedMotion()) {
      tauRef.current = Infinity;
      schedule();
      return;
    }
    const [t0, t1] = domain();
    stopPlayback(false);
    setPlaying(true);
    const startedAt = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, (now - startedAt) / PLAY_MS);
      tauRef.current = t0 + (t1 - t0) * p;
      drawRef.current();
      if (p < 1 && playRef.current) {
        playRef.current.raf = requestAnimationFrame(step);
      } else {
        tauRef.current = Infinity;
        playRef.current = null;
        setPlaying(false);
        drawRef.current();
      }
    };
    playRef.current = { raf: requestAnimationFrame(step), startedAt };
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const positionsHit = (mx: number, my: number): number => {
      // mirror of the draw-pass placement math AND its gutter clip — atoms
      // panned under the lane labels are not drawn, so they must not be hit.
      if (mx < GUTTER) return -1;
      const { atoms, visible, lanes, laneIndexOf } = propsRef.current;
      const rect = canvas.getBoundingClientRect();
      const [t0, t1] = domain();
      const plotW = Math.max(50, rect.width - GUTTER - 16);
      const plotH = Math.max(50, rect.height - TOP - AXIS_H);
      const laneH = plotH / lanes.length;
      const cam = camRef.current;
      const tau = Math.min(tauRef.current, t1);
      let best = -1;
      let bestD = Infinity;
      for (let i = 0; i < atoms.length; i++) {
        if (!visible[i]) continue;
        const a = atoms[i];
        const t = a.created_at || t0;
        if (t > tau) continue;
        const x = GUTTER + ((t - t0) / (t1 - t0)) * plotW * cam.kx + cam.panX;
        if (x < GUTTER) continue; // clipped out of the plot in the draw pass
        const li = laneIndexOf(a.type);
        const y = TOP + li * laneH + laneH / 2 + (hash01(a.key) - 0.5) * laneH * 0.64;
        const d = Math.hypot(x - mx, y - my);
        if (d < Math.max(8, atomRadius(a.chars, 1) * 0.85 + 3) && d < bestD) {
          bestD = d;
          best = i;
        }
      }
      return best;
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const cam = camRef.current;
      const kx = Math.min(30, Math.max(1, cam.kx * Math.exp(-e.deltaY * 0.0016)));
      // keep the time under the cursor fixed
      const rel = mx - GUTTER - cam.panX;
      const scale = kx / cam.kx;
      camRef.current = { kx, panX: mx - GUTTER - rel * scale };
      // clamp pan so the plot never detaches from the gutter
      const plotW = Math.max(50, rect.width - GUTTER - 16);
      camRef.current.panX = Math.min(0, Math.max(-(plotW * kx - plotW), camRef.current.panX));
      schedule();
    };
    const onDown = (e: PointerEvent) => {
      // Left/primary only — a right-click's pointerup is swallowed by the
      // context menu, which would leave the view stuck in pan mode.
      if (e.button !== 0 || !e.isPrimary) return;
      canvas.setPointerCapture(e.pointerId);
      dragRef.current = { on: true, moved: false, x: e.offsetX };
    };
    const onCancel = () => {
      dragRef.current.on = false;
    };
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (d.on) {
        const dx = e.offsetX - d.x;
        if (Math.abs(dx) > 2) d.moved = true;
        const rect = canvas.getBoundingClientRect();
        const plotW = Math.max(50, rect.width - GUTTER - 16);
        const cam = camRef.current;
        cam.panX = Math.min(0, Math.max(-(plotW * cam.kx - plotW), cam.panX + dx));
        d.x = e.offsetX;
        propsRef.current.onHover(null);
        schedule();
        return;
      }
      const hit = positionsHit(e.offsetX, e.offsetY);
      if (hit !== hoverRef.current) {
        hoverRef.current = hit;
        schedule();
      }
      if (hit >= 0) {
        const a = propsRef.current.atoms[hit];
        propsRef.current.onHover({
          x: e.offsetX,
          y: e.offsetY,
          title: a.title,
          meta: `${a.type} · ${a.scope} · ${ageLabel(a.created_at)}`,
          swatch: propsRef.current.colorOf(a),
        });
      } else {
        propsRef.current.onHover(null);
      }
    };
    const onUp = (e: PointerEvent) => {
      const d = dragRef.current;
      if (d.on && !d.moved) {
        const hit = positionsHit(e.offsetX, e.offsetY);
        propsRef.current.onSelect(hit >= 0 ? propsRef.current.atoms[hit] : null);
        schedule();
      }
      dragRef.current.on = false;
    };
    const onLeave = () => {
      dragRef.current.on = false;
      if (hoverRef.current !== -1) {
        hoverRef.current = -1;
        propsRef.current.onHover(null);
        schedule();
      }
    };

    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointerleave", onLeave);
    canvas.addEventListener("pointercancel", onCancel);
    canvas.addEventListener("lostpointercapture", onCancel);
    const ro = new ResizeObserver(schedule);
    ro.observe(canvas);
    return () => {
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointerleave", onLeave);
      canvas.removeEventListener("pointercancel", onCancel);
      canvas.removeEventListener("lostpointercapture", onCancel);
      ro.disconnect();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0; // reset the guard or StrictMode remount deadlocks schedule()
      }
      if (playRef.current) {
        cancelAnimationFrame(playRef.current.raf);
        playRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <canvas ref={canvasRef} style={{ cursor: "crosshair" }} aria-label="Timeline of brain atoms by type" role="img" />
      <button
        className="atlas-btn atlas-play"
        onClick={() => (playing ? stopPlayback(true) : startPlayback())}
        aria-label={playing ? "Skip to end of growth replay" : "Replay knowledge growth"}
      >
        <Icon name={playing ? "player-skip-forward" : "player-play"} size={13} />
        {playing ? "Skip" : "Replay growth"}
      </button>
    </>
  );
}
